import { Component, h, Host, Prop, State, Watch } from "@stencil/core";
import { SpinnerOverlayStatusEnum, SpinnerOverlayTransitionStatusType } from "./spinner-overlay-status";
import { LoaderVariant } from "../loader/loader.enum";

@Component({
    tag: "ux-spinner-overlay",
    styleUrl: "spinner-overlay.scss",
    shadow: true
})
export class SpinnerOverlay {
    /**
     * Represents whether the spinner-overlay is currently open.
     * @prop {boolean} opened - A boolean property that reflects the current state of the spinner-overlay.
     * If `true`, the spinner-overlay is open. If `false`, the spinner-overlay is closed. Default value is `false`.
     */
    @Prop({ reflect: true }) readonly opened: boolean = false;

    /**
     * Represents the headline displayed in the spinner.
     * @prop {string} headline - A string property that sets the headline
     * This headline provides a brief, concise description of the loading state
     */
    @Prop() readonly headline!: string;

    /**
     * Represents the subline displayed in the spinner.
     * @prop {string} subline - A string property that sets the subline
     * This subline provides additional information or details about the loading state
     */
    @Prop() readonly subline!: string;

    /** The spinner-overlay transition status */
    @State() status: SpinnerOverlayTransitionStatusType = SpinnerOverlayStatusEnum.CLOSED;

    // @ts-expect-error Check for typing
    private transitionTimeout: ReturnType<typeof setTimeout> = null;

    @Watch("opened")
    setOverlayStatus() {
        if (this.transitionTimeout !== null) {
            clearTimeout(this.transitionTimeout);
        }
        if (this.opened) {
            if (this.status === SpinnerOverlayStatusEnum.OPENED) {
                return;
            }
            this.status = SpinnerOverlayStatusEnum.OPENING;
            this.transitionTimeout = setTimeout(() => {
                this.status = SpinnerOverlayStatusEnum.OPENED;
            }, 100);
        } else {
            if (this.status === SpinnerOverlayStatusEnum.CLOSED) {
                return;
            }
            this.status = SpinnerOverlayStatusEnum.CLOSING;
            this.transitionTimeout = setTimeout(() => {
                this.status = SpinnerOverlayStatusEnum.CLOSED;
            }, 350);
        }
    }

    componentWillLoad() {
        this.setOverlayStatus();
    }

    render() {
        const renderSpinner = this.status !== SpinnerOverlayStatusEnum.CLOSED;
        const displayMessage = this.headline || this.subline;
        return (
            <Host status={this.status} role="alert" aria-busy="true">
                <div class="backdrop">
                    {renderSpinner && !displayMessage && <ux-loader class="loader" variant={LoaderVariant.Spinner}></ux-loader>}
                    {renderSpinner && displayMessage && (
                        <div class="container">
                            <h2 class="headline">{this.headline}</h2>
                            <ux-loader variant={LoaderVariant.Spinner}></ux-loader>
                            <p class="subline">{this.subline}</p>
                        </div>
                    )}
                </div>
            </Host>
        );
    }
}
